import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Download, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import AppShell from "@/components/layout/AppShell";
import CostFilters from "@/components/costs/CostFilters";
import KpiCards from "@/components/costs/KpiCards";
import CreditsLegend from "@/components/costs/CreditsLegend";
import DailyChart from "@/components/costs/DailyChart";
import { Breakdown, modelName } from "@/components/costs/Breakdown";
import ProjectsTable from "@/components/costs/ProjectsTable";
import OperationsTable from "@/components/costs/OperationsTable";
import { presets } from "@/components/costs/helpers";
import { downloadFile, errMsg, useApi } from "@/lib/api";

const KEYS = ["start", "end", "project", "category", "provider", "source"];

export default function Costs() {
  const api = useApi();
  const [sp, setSp] = useSearchParams();
  const [, , mStart, today] = presets()[0];
  const f = { start: sp.get("start") || mStart, end: sp.get("end") || today, project: sp.get("project") || "", category: sp.get("category") || "", provider: sp.get("provider") || "", source: sp.get("source") || "" };
  const set = (patch) => { const n = { ...f, ...patch }; setSp(Object.fromEntries(KEYS.filter((k) => n[k]).map((k) => [k, n[k]])), { replace: true }); };
  const params = useMemo(() => ({ start: f.start, end: f.end, project_id: f.project, category: f.category, provider: f.provider, key_source: f.source }), [sp.toString()]); // eslint-disable-line react-hooks/exhaustive-deps
  const [sum, setSum] = useState(null);
  const [projects, setProjects] = useState([]);
  const [exporting, setExporting] = useState("");
  const load = useCallback(() => api.get("/costs/summary", { params }).then(({ data }) => setSum(data)).catch((e) => { toast.error(errMsg(e)); setSum(false); }), [api, params]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/projects").then(({ data }) => setProjects(data)).catch(() => {}); }, [api]);
  const exportCsv = async (what) => {
    setExporting(what);
    const qs = new URLSearchParams({ ...params, what }).toString();
    try { await downloadFile(api, `/costs/export?${qs}`, `costi-ai-${what}.csv`); } catch (e) { toast.error(errMsg(e)); }
    setExporting("");
  };

  return (
    <AppShell>
      <div className="px-5 sm:px-8 lg:px-12 py-8 lg:py-12 max-w-7xl space-y-6" data-testid="costs-page">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-sky-700 font-medium">Controllo spesa AI</p>
            <h1 className="mt-2 text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">Costi e utilizzo</h1>
            <p className="mt-2 text-sm text-slate-500 max-w-2xl">Tutti gli importi sono USD stimati: token registrati × listino configurato al momento della chiamata. Non sono addebiti reali.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {[["operations", "CSV operazioni"], ["calls", "CSV chiamate"]].map(([w, l]) => (
              <Button key={w} size="sm" variant="outline" onClick={() => exportCsv(w)} disabled={!!exporting} data-testid={`costs-export-${w}-btn`}>
                {exporting === w ? <Loader2 size={13} className="mr-1.5 animate-spin" /> : <Download size={13} className="mr-1.5" />}{l}
              </Button>
            ))}
          </div>
        </div>
        <CostFilters f={f} set={set} projects={projects} categories={sum?.categories || {}} providers={sum?.providers || {}} />
        {sum === null && <p className="flex items-center gap-2 text-sm text-slate-500"><Loader2 size={14} className="animate-spin" />Caricamento…</p>}
        {sum === false && <p className="text-sm text-red-600" data-testid="costs-error">Dati non disponibili.</p>}
        {sum && (
          <>
            <KpiCards sum={sum} />
            <CreditsLegend sum={sum} />
            <DailyChart sum={sum} />
            <div className="grid gap-4 lg:grid-cols-2">
              <Breakdown title="Per tipo di operazione" rows={sum.by_category} name={(r) => r.label} testid="costs-by-category" />
              <Breakdown title="Per provider, modello e fonte" rows={sum.by_model} name={modelName(sum.providers)} testid="costs-by-model" />
            </div>
            <ProjectsTable rows={sum.by_project} onChanged={load} />
          </>
        )}
        <OperationsTable params={params} />
      </div>
    </AppShell>
  );
}
